import React, { useState, useRef, useEffect } from 'react';
import {
    Box, Button, Typography, List, ListItem, ListItemText, IconButton,
    Dialog, DialogTitle, DialogContent, Pagination, CircularProgress,
    TextField, InputAdornment, Chip, Select, MenuItem, FormControl,
    InputLabel, OutlinedInput, Popover, Tooltip,
    Stack,
} from '@mui/material';
import { Paper } from '@shared/ui/common/paper';
import PageLayout from '@shared/shell/Layout/PageLayout';
import { Add, Close, Search, SwapHoriz } from '@mui/icons-material';
import { MarketplaceGrid } from '@features/connections/generic/components/MarketplaceGrid';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { useAuthStore } from '../../../application/store/AuthStore';
import { useAuth } from '@shared/hooks/useAuth';
import { Company } from '@domain/account/models/Company';
import { useToast } from '@shared/hooks/useToast';
import { APP_CONFIG } from '../../../config/app.config';
import { useCompany } from '../hooks/useCompany';
import EmptyState from '@shared/ui/common/EmptyState';
import { CompanySubscriptionCard } from '@features/subscription';
import ImpersonationPanel from './ImpersonationPanel';
import { useQueryClient } from '@tanstack/react-query';
import { useConnections } from '@shared/hooks';
import { ConnectionListItem } from '@features/connections/generic/components/ConnectionListItem';
import { useGenericDrawer } from '@shared/shell/drawer/useGenericDrawer';
import { useDebounce } from '@shared/hooks/useDebounce';
import { useSearchParams } from 'react-router-dom';

const SUBSCRIPTION_STATUSES = ['active', 'trialing', 'past_due', 'unpaid', 'canceled', 'incomplete', 'paused'];

const CompanyPage = () => {

    const [isLoading, setIsLoading] = useState(false);
    const [isAddConnectionOpen, setIsAddConnectionOpen] = useState(false);
    const [switchAnchorEl, setSwitchAnchorEl] = useState<HTMLElement | null>(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 25;

    // Filter state
    const [searchInput, setSearchInput] = useState('');
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
    const debouncedSearch = useDebounce(searchInput, 300);

    const { connections } = useConnections();
    const { openDrawer } = useGenericDrawer();
    const { companies, subscriptionStatuses, currentCompany, isLoading: isLoadingCompanies } = useCompany(
        currentPage,
        itemsPerPage,
        debouncedSearch || undefined,
        selectedStatuses.length > 0 ? selectedStatuses : undefined
    );

    const currentCompanyId = useAuthStore(state => state.companyId);
    const setAuth = useAuthStore(state => state.setAuth);
    const { login } = useAuth();
    const { user } = useAuth();
    const toast = useToast();
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const handledQueryRef = useRef(false);

    useEffect(() => {
        if (handledQueryRef.current) return;

        const billing = searchParams.get('billing');
        const shopifyConnected = searchParams.get('shopify_connected');
        const shopifyError = searchParams.get('shopify_error');

        if (!billing && !shopifyConnected && !shopifyError) return;

        handledQueryRef.current = true;

        if (billing === 'success') {
            toast.success('Your Shopify subscription is now active.');
        } else if (billing === 'error') {
            toast.error('We could not confirm your Shopify subscription. Please try again.');
        }

        if (shopifyConnected === '1') {
            toast.success('Shopify store connected successfully.');
            queryClient.invalidateQueries({ queryKey: ['connections'] });
        }

        if (shopifyError) {
            toast.error(shopifyError);
        }

        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('billing');
        nextParams.delete('shopify_connected');
        nextParams.delete('shopify_error');
        setSearchParams(nextParams, { replace: true });
    }, [searchParams, setSearchParams, toast, queryClient]);

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page - 1);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(e.target.value);
        setCurrentPage(0);
    };

    const handleOpenSwitch = (e: React.MouseEvent<HTMLElement>) => {
        setSwitchAnchorEl(e.currentTarget);
    };

    const handleCloseSwitch = () => {
        setSwitchAnchorEl(null);
        setSearchInput('');
        setSelectedStatuses([]);
        setCurrentPage(0);
    };

    const handleCompanyChange = async (company: Company) => {
        try {
            setIsLoading(true);
            if (!user) throw new Error('No user available');

            const token = await useAuthStore.getState().getJwt();
            if (!token) throw new Error('No token available');

            setAuth(user.id, token, company.id || undefined);
            await login(user, token, company);

            await queryClient.invalidateQueries({ queryKey: ['connections'] });
            queryClient.removeQueries({ queryKey: ['subscription'] });
            queryClient.removeQueries({ queryKey: ['usage-limits'] });
            await queryClient.invalidateQueries({ queryKey: ['subscription'] });
            await queryClient.invalidateQueries({ queryKey: ['usage-limits'] });

            toast.success(`Switched to company: ${company.name}`);
            handleCloseSwitch();
        } catch (err) {
            toast.error('Failed to switch company');
            if (user) {
                const token = await useAuthStore.getState().getJwt();
                if (token) setAuth(user.id, token, currentCompanyId || undefined);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const switchOpen = Boolean(switchAnchorEl);

    return (
        <PageLayout title="Companies">
            {/* Current company */}
            <Stack direction="column" spacing={4}>
                <Dialog
                    open={isAddConnectionOpen}
                    onClose={() => setIsAddConnectionOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ borderBottom: `1px solid ${APP_CONFIG.ui.theme.colors.border}`, p: 3, justifyContent: 'space-between', display: 'flex', alignItems: 'center' }}>
                        <Typography variant="h6">Add a connection</Typography>
                        <IconButton onClick={() => setIsAddConnectionOpen(false)}>
                            <Close />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ p: 0, pt: 2 }}>
                            <MarketplaceGrid onNewConnection={() => setIsAddConnectionOpen(false)} />
                        </Box>
                    </DialogContent>
                </Dialog>

                {/* Current company */}
                <Paper>
                    <DialogHeader>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="h6">Company</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ImpersonationPanel />
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<SwapHoriz />}
                                    onClick={handleOpenSwitch}
                                >
                                    Switch
                                </Button>
                            </Box>
                        </Box>
                    </DialogHeader>

                    <Box sx={{ px: 3, py: 2 }}>
                        {currentCompany ? (
                            <Tooltip title={currentCompany.id} placement="top-start">
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    sx={{ cursor: 'pointer', display: 'inline-block' }}
                                    onClick={() => navigator.clipboard.writeText(currentCompany.id)}
                                >
                                    {currentCompany.name}
                                </Typography>
                            </Tooltip>
                        ) : (
                            <Typography variant="body2" color="text.secondary">No company selected</Typography>
                        )}
                    </Box>

                    <Popover
                        open={switchOpen}
                        anchorEl={switchAnchorEl}
                        onClose={handleCloseSwitch}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{ sx: { width: 520, maxHeight: 560, display: 'flex', flexDirection: 'column' } }}
                    >
                        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${APP_CONFIG.ui.theme.colors.border}` }}>
                            <TextField
                                size="small"
                                placeholder="Search by name..."
                                value={searchInput}
                                onChange={handleSearchChange}
                                autoFocus
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ flex: 1 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 170 }}>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    multiple
                                    value={selectedStatuses}
                                    onChange={e => {
                                        setSelectedStatuses(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value);
                                        setCurrentPage(0);
                                    }}
                                    input={<OutlinedInput label="Status" />}
                                    renderValue={selected => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {(selected as string[]).map(v => (
                                                <Chip key={v} label={v.replace('_', ' ')} size="small" />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {SUBSCRIPTION_STATUSES.map(status => (
                                        <MenuItem key={status} value={status}>
                                            {status.replace('_', ' ')}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {isLoadingCompanies && <CircularProgress size={18} />}
                        </Box>

                        <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
                            {!isLoadingCompanies && companies?.length === 0 && (
                                <Box sx={{ p: 3, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">No companies found</Typography>
                                </Box>
                            )}
                            {companies?.map((company) => {
                                const sub = subscriptionStatuses.find(s => s.companyId === company.id);
                                const subStatus = sub?.status ?? null;
                                const isCurrent = company.id === currentCompanyId;
                                return (
                                    <ListItem
                                        key={company.id}
                                        divider
                                        sx={{ px: 2, py: 1 }}
                                        secondaryAction={
                                            isCurrent ? (
                                                <Chip label="Current" size="small" color="primary" />
                                            ) : (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => handleCompanyChange(company)}
                                                    disabled={isLoading}
                                                >
                                                    Switch
                                                </Button>
                                            )
                                        }
                                    >
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Tooltip title={company.id} placement="top-start">
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={isCurrent ? 600 : 400}
                                                            sx={{ cursor: 'pointer' }}
                                                            onClick={() => navigator.clipboard.writeText(company.id)}
                                                        >
                                                            {company.name}
                                                        </Typography>
                                                    </Tooltip>
                                                    {subStatus && (
                                                        <Chip
                                                            label={subStatus.replace('_', ' ')}
                                                            size="small"
                                                            color={
                                                                subStatus === 'active' ? 'success' :
                                                                    subStatus === 'trialing' ? 'info' :
                                                                        subStatus === 'past_due' ? 'warning' : 'default'
                                                            }
                                                        />
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>

                        {companies?.length && companies?.length > itemsPerPage && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1, borderTop: `1px solid ${APP_CONFIG.ui.theme.colors.border}` }}>
                                <Pagination
                                    count={Math.ceil(companies?.length / itemsPerPage)}
                                    page={currentPage + 1}
                                    onChange={handlePageChange}
                                    color="primary"
                                    size="small"
                                    disabled={isLoadingCompanies}
                                />
                            </Box>
                        )}
                    </Popover>
                </Paper>

                {/* Connections */}
                <Paper>
                    <DialogHeader>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="h6">Connections</Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Add />}
                                onClick={() => setIsAddConnectionOpen(true)}
                            >
                                Add Connection
                            </Button>
                        </Box>
                    </DialogHeader>

                    <List disablePadding={true} sx={{ pb: 0 }}>
                        {connections?.length === 0 && (
                            <EmptyState
                                title="No marketplace connections"
                                description="You don't have any marketplace connections yet. Add a connection to get started."
                                action={{
                                    label: 'Add Connection',
                                    onClick: () => setIsAddConnectionOpen(true)
                                }}
                            />
                        )}
                        {connections?.map((connection) => (
                            <ConnectionListItem
                                key={connection.id}
                                connection={connection}
                                onClick={(settings) => openDrawer(connection.name, settings)}
                            />
                        ))}
                    </List>
                </Paper>

                {/* Subscription */}
                <CompanySubscriptionCard />

            </Stack>
        </PageLayout>
    );
};

export default CompanyPage;
